import { createTheme } from '@mui/material/styles';
import type { PaletteMode } from '@mui/material';

// Flutter-inspired color palette
export const flutterColors = {
  // Primary blues (from Flutter blue palette)
  primary: {
    main: '#42A5F5',      // blue[400]
    light: '#90CAF9',     // blue[200]
    dark: '#1976D2',      // blue[700]
    contrastText: '#FFFFFF',
  },
  // Secondary for accents
  secondary: {
    main: '#1976D2',      // blue[700]
    light: '#42A5F5',
    dark: '#0D47A1',      // blue[900]
    contrastText: '#FFFFFF',
  },
  // Background colors (Flutter uses white surfaces)
  background: {
    default: '#FAFAFA',   // Slightly off-white
    paper: '#FFFFFF',
  },
  // Text colors
  text: {
    primary: '#212121',   // grey[900]
    secondary: '#757575', // grey[600]
  },
  // Status colors matching Flutter
  error: {
    main: '#EF5350',      // red[400]
    light: '#E57373',
    dark: '#D32F2F',
  },
  warning: {
    main: '#FFB74D',      // orange[300]
    light: '#FFCC80',
    dark: '#F57C00',
  },
  success: {
    main: '#66BB6A',      // green[400]
    light: '#81C784',
    dark: '#388E3C',
  },
  // Grey scale for dividers, borders
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  // Flutter-style divider
  divider: '#E0E0E0',
};

// Dark-mode overrides layered over the shared palette
export const flutterDarkColors = {
  ...flutterColors,
  background: {
    default: '#121212',
    paper: '#1E1E1E',
  },
  text: {
    primary: '#EEEEEE',   // grey[200]
    secondary: '#9E9E9E', // grey[500]
  },
  divider: '#424242',     // grey[800]
};

// Create the MUI theme
export const createFlutterTheme = (mode: PaletteMode = 'light') => createTheme({
  palette: {
    mode,
    ...(mode === 'dark' ? flutterDarkColors : flutterColors),
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    // Match Flutter's Material 3 typography
    h4: {
      fontWeight: 400,
      fontSize: '2.125rem',
      lineHeight: 1.235,
    },
    h5: {
      fontWeight: 400,
      fontSize: '1.5rem',
      lineHeight: 1.334,
    },
    h6: {
      fontWeight: 500,
      fontSize: '1.25rem',
      lineHeight: 1.6,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.43,
      fontWeight: 400,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12, // Flutter Material 3 uses 12px
  },
  spacing: 8,
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'dark' ? flutterDarkColors.background.paper : flutterColors.primary.main,
          color: flutterColors.primary.contrastText,
          boxShadow: 'none',
          elevation: 0,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 20, // Flutter uses pill-shaped buttons
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: '0px 1px 3px rgba(0,0,0,0.12)',
        },
        elevation1: {
          boxShadow: '0px 1px 3px rgba(0,0,0,0.12)',
        },
        elevation2: {
          boxShadow: '0px 2px 4px rgba(0,0,0,0.14)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 500,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

// Breakpoints for responsive design
export const breakpoints = {
  mobile: 0,
  tablet: 768,
  desktop: 1024,
  wide: 1440,
};

// Responsive helper functions
export const getResponsiveValue = <T,>(
  _isMobile: boolean,
  isTablet: boolean,
  isDesktop: boolean,
  values: { mobile: T; tablet: T; desktop: T }
): T => {
  if (isDesktop) return values.desktop;
  if (isTablet) return values.tablet;
  return values.mobile;
};

export default createFlutterTheme;
